// src/pages/doctor/DoctorReservations.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { FaTruck, FaFileCsv } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ZoomHowToModal from '../../components/ZoomHowToModal';

/** =========================
 * 型定義
 * ========================= */
interface Reservation {
  id: number;
  user_email: string | null;
  user_name?: string;
  presence?: string;         // 'online' | 'offline'（患者在席ランプ用）
  service: string;
  visit_type?: string;
  date: string;
  time: string;
  status: string; // 'assigned' | 'completed' | 'canceled' | 'doctor_cancelled' | 'admin_cancelled' | 'expired' | 'pending'
  needs_translation?: boolean;
  questionnaire_completed: boolean;
  diagnosis?: string;
  prescription_text?: string;
  notes?: string;
  zoom_join_url: string | null;
  zoom_start_url?: string | null;
  zoom_meeting_id?: string | null;
  isOverdue?: boolean;
  tracking_number?: string | null;
  shipping_carrier?: string | null;

  // ★ 支払関連
  payment_status?: string | null;
  total_amount_jpy?: number | null;
  transaction_id?: string | null;
  receipt_url?: string | null;

  user_profile?: {
    full_name?: string;
    furigana?: string;
    gender?: string;
    birth_date?: string;
    phone_number?: string;
    postal_code?: string;
    address?: string;
    karte_number?: string;
    chart_number?: string;
    medical_record_number?: string;
    karteNo?: string;
    mrn?: string;
    patient_code?: string;
    patient_id?: string;
  };
}

/** =========================
 * LocalStorage 用キー
 * ========================= */
const STORAGE_KEY_SORT = 'doctorReservations.sortOrder';
const STORAGE_KEY_PAGESIZE = 'doctorReservations.pageSize';
const STORAGE_KEY_COLLAPSE = 'doctorReservations.collapsedGroups';
const STORAGE_KEY_PAGEBY = 'doctorReservations.pageByGroup';

/** 安全な JSON 解析（失敗時は null） */
const safeParseJSON = <T,>(s: string | null): T | null => {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
};

/** ===== 记录填写状态计算（新增） ===== */
type TrafficColor = 'green' | 'yellow' | 'red';
const isEmptyField = (v: any) => v == null || String(v).trim() === '';

const useRecordStatus = () => {
  const { t } = useTranslation();
  const fieldDefs = [
    { key: 'diagnosis' as const, label: t('doctor.diagnosis') || '診断' },
    { key: 'prescription_text' as const, label: t('doctor.prescription') || '処方' },
    { key: 'notes' as const, label: t('doctor.notes') || 'メモ' },
  ];
  const getStatus = (r: Reservation) => {
    const missing = fieldDefs
      .filter(fd => isEmptyField((r as any)[fd.key]))
      .map(fd => fd.label);
    let color: TrafficColor = 'yellow';
    if (missing.length === 0) color = 'green';
    else if (missing.length === fieldDefs.length) color = 'red';
    const title = missing.length
      ? `${t('doctor.missing_fields') || '未入力'}: ${missing.join(', ')}`
      : (t('doctor.all_filled') || 'すべて入力済み');
    return { color, missing, title };
  };
  return { getStatus };
};

/** 小圆点组件 */
const Dot: React.FC<{ color: TrafficColor; title?: string }> = ({ color, title }) => {
  const colorCls =
    color === 'green'
      ? 'bg-green-600'
      : color === 'yellow'
        ? 'bg-amber-500'
        : 'bg-red-600';
  return (
    <span
      title={title}
      className={`inline-block w-2.5 h-2.5 rounded-full ${colorCls}`}
      aria-label={title}
    />
  );
};

/** 患者在席ランプ（緑=オンライン / 灰=オフライン）。予約一覧で患者名の左に表示。 */
const PresenceDot: React.FC<{ presence?: string }> = ({ presence }) => {
  const online = presence === 'online';
  return (
    <span
      title={online ? '在席中（オンライン）' : 'オフライン'}
      aria-label={online ? 'online' : 'offline'}
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${online ? 'bg-green-500' : 'bg-gray-300'}`}
    />
  );
};

/** Zoom 入室块（刷新按钮在这里） */
interface ZoomEntryProps {
  joinUrl: string | null | undefined;
  meetingId?: string | null | undefined;
}

const ZoomEntry: React.FC<ZoomEntryProps> = ({ joinUrl, meetingId }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase();
  const isJa = lang.startsWith('ja');
  const oneLineHint = isJa
    ? '※ 開始時にアプリを促されたら「キャンセル」→ ページ内の「ブラウザから参加」をクリックしてください。'
    : '※ 弹窗提示应用时，请先点“取消”，再点击页面中的“从浏览器加入”。';

  return (
    <div className="flex flex-col items-center gap-1 text-[11px] leading-snug">
      {joinUrl ? (
        <div className="flex flex-col items-center gap-1 w-full text-center">
          {meetingId && (
            <div className="bg-gray-50 px-1.5 py-0.5 rounded-lg text-gray-400 font-mono text-[10px] scale-90">
              ID: {meetingId}
            </div>
          )}
          <div className="flex items-center gap-1.5 justify-center">
            <a
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-coral text-white px-6 py-2.5 rounded-full hover:shadow-md transition-all text-sm font-bold inline-block w-40 text-center shadow-soft active:scale-95"
            >
              {isJa ? '診察室に入る' : '进入诊查室'}
            </a>
          </div>
          <div className="text-gray-400 text-[10px] leading-tight text-center max-w-[180px] opacity-80 mt-0.5 whitespace-pre-line">
            {isJa
              ? '※ 開始時にアプリを促されたら「キャンセル」→ ページ内の「ブラウザから参加」をクリックしてください。'
              : '※ 弹窗提示应用时，请先点“取消”，再点击页面中的“从浏览器加入”。'}
          </div>
        </div>
      ) : (
        <span className="text-gray-400 text-xs">-</span>
      )}
      <div className="flex items-center gap-3 mt-0.5">
        <button
          type="button"
          onClick={() => {
            const ev = new CustomEvent('openZoomHowTo');
            window.dispatchEvent(ev);
          }}
          className="text-coral hover:text-coral underline"
        >
          {isJa ? '手順を見る' : '查看步骤'}
        </button>
      </div>
    </div>
  );
};

const DoctorReservations: React.FC = () => {
  /** 预约列表数据 */
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [pool, setPool] = useState<Reservation[]>([]);

  /** 取消、档案、记录、退回、Claim、Zoom 等状态 */
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingTarget, setTrackingTarget] = useState<Reservation | null>(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('yamato');
  const [cancelReason, setCancelReason] = useState('');

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState<Reservation | null>(null);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordTarget, setRecordTarget] = useState<Reservation | null>(null);

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnTargetId, setReturnTargetId] = useState<number | null>(null);
  const [returnReasonCode, setReturnReasonCode] = useState('');
  const [returnReasonText, setReturnReasonText] = useState('');

  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimTargetId, setClaimTargetId] = useState<number | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);


  const [showZoomHowTo, setShowZoomHowTo] = useState(false);

  /** 折叠、排序、分页 */
  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({});
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pageByGroup, setPageByGroup] = useState<{ [key: string]: number }>({});
  const [pageSize, setPageSize] = useState<number>(5);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { getStatus } = useRecordStatus();

  /** 开始后即算「过期」，与后端逻辑对齐（宽限 0 分钟） */
  const EXPIRY_GRACE_MINUTES = 0;

  /** 解析日期时间到 ms */
  const parseDateTime = (dateStr: string, timeStr: string): number => {
    const ds = dateStr.replace(/\./g, '/').replace(/-/g, '/');
    const [y, m, d] = ds.split('/').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!y || !m || !d || isNaN(hh) || isNaN(mm)) return 0;
    const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
    return dt.getTime();
  };

  /** カルテ番号 */
  const getKarteNumber = (r: Reservation): string => {
    const p = r.user_profile || {};
    const candidates = [
      (p as any).karte_number,
      (p as any).chart_number,
      (p as any).medical_record_number,
      (p as any).karteNo,
      (p as any).mrn,
      (p as any).patient_code,
      (p as any).patient_id,
    ];
    const first = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
    return first || '-';
  };

  /** 支払状況バッジ(色分けで視認性を上げる。admin と同系色: 支払済=緑 / 未払い=琥珀 / 金額未設定=灰) */
  const renderPaymentStatusBadge = (r: Reservation) => {
    const amount = r.total_amount_jpy ?? 0;
    const status = (r.payment_status || '').toLowerCase();

    let label: string;
    let cls: string;
    if (!amount || amount <= 0) {
      label = t('doctor.payment_none') || '金額未設定';
      cls = 'bg-gray-100 text-gray-500 border-gray-200';
    } else if (status === 'paid') {
      label = t('doctor.payment_paid') || '支払済み';
      cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    } else {
      label = t('doctor.payment_unpaid') || '未払い';
      cls = 'bg-amber-50 text-amber-700 border-amber-200';
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-bold whitespace-nowrap ${cls}`}>
        {label}
      </span>
    );
  };

  const renderTranslationNeedText = (r: Reservation): string => {
    return r.needs_translation
      ? (t('reserve.translation_required_yes') || '需要中文翻译')
      : (t('reserve.translation_required_no') || '不需要翻译');
  };

  /** 自分の予約一覧 */
  const fetchReservations = async () => {
    try {
      const res = await api.get('/doctor/reservations');
      const now = new Date();
      const currentTs = now.getTime();
      // 获取今日凌晨 00:00:00 的时间戳，用于日期判断
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

      const updated: Reservation[] = (res.data as Reservation[]).map((r) => {
        const ts = parseDateTime(r.date, r.time);
        
        // 逻辑A：如果是之前的日期（次日判定），则强制设为过期
        if (r.status === 'assigned' && ts < todayStart) {
          return { ...r, status: 'expired' };
        }
        
        // 逻辑B：如果是今日且时间已过，保持 assigned 状态，但打上 isOverdue 标记用于 UI
        if (r.status === 'assigned' && currentTs > ts) {
          return { ...r, isOverdue: true };
        }
        
        return r;
      });
      setReservations(updated);
    } catch (err: any) {
      console.error('Error fetching reservations:', err);
    }
  };

  /** 预约池 */
  const fetchPool = async () => {
    try {
      // 1st: require_shift=true
      const res1 = await api.get('/doctor/pool', {
        params: { include_past: 1, past_grace_min: 60, require_shift: true },
      });
      let data = res1.data as any[];

      // 回退：require_shift=false（シフト一致の予約が無ければ全未割当を表示）
      if (!Array.isArray(data) || data.length === 0) {
        try {
          const res2 = await api.get('/doctor/pool', {
            params: { include_past: 1, past_grace_min: 60, require_shift: false },
          });
          data = res2.data as any[];
        } catch {
          // 回退リクエストが失敗した場合は最初の結果のまま
        }
      }

      const normalized: Reservation[] = (data || []).map((r: any) => ({
        id: r.id,
        user_email: r.user_email ?? null,
        user_name: r.user_name,
        presence: r.presence,
        service: r.service,
        visit_type: r.visit_type,
        date: r.date,
        time: r.time,
        status: 'pending',
        needs_translation: !!r.needs_translation,
        questionnaire_completed: !!(r.questionnaire_completed ?? r.is_questionnaire_filled ?? false),
        diagnosis: r.diagnosis,
        prescription_text: r.prescription_text,
        notes: r.notes,
        zoom_join_url: null,
        zoom_start_url: null,
        user_profile: r.user_profile || undefined,
        payment_status: r.payment_status ?? null,
        total_amount_jpy: r.total_amount_jpy ?? null,
        transaction_id: r.transaction_id ?? null,
        receipt_url: r.receipt_url ?? null,
      }));

      setPool(normalized);
    } catch (err) {
      console.error('Error fetching pool:', err);
    }
  };

  /** Zoom HowTo Modal を ZoomEntry から開けるように */
  useEffect(() => {
    const handler = () => setShowZoomHowTo(true);
    window.addEventListener('openZoomHowTo', handler);
    return () => window.removeEventListener('openZoomHowTo', handler);
  }, []);

  /** 初期ロード & 設定復元 */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSort = params.get('sort');
    const urlPs = params.get('ps');
    const urlCg = params.get('cg');
    const urlPg = params.get('pg');

    let effectiveSort: 'asc' | 'desc' = 'desc';
    let effectivePs = 5;
    let effectiveCg: { [key: string]: boolean } = { canceled: true, expired: true };
    let effectivePg: { [key: string]: number } = {};

    if (urlSort === 'asc' || urlSort === 'desc') {
      effectiveSort = urlSort;
    } else {
      const savedSort = localStorage.getItem(STORAGE_KEY_SORT) as 'asc' | 'desc' | null;
      if (savedSort === 'asc' || savedSort === 'desc') effectiveSort = savedSort;
    }

    if (urlPs && [5, 10, 20].includes(Number(urlPs))) {
      effectivePs = Number(urlPs);
    } else {
      const savedPageSize = Number(localStorage.getItem(STORAGE_KEY_PAGESIZE));
      if ([5, 10, 20].includes(savedPageSize)) effectivePs = savedPageSize;
    }

    const urlCgObj = safeParseJSON<{ [key: string]: boolean }>(urlCg);
    if (urlCgObj && typeof urlCgObj === 'object') {
      effectiveCg = urlCgObj;
    } else {
      const savedCg = safeParseJSON<{ [key: string]: boolean }>(localStorage.getItem(STORAGE_KEY_COLLAPSE));
      if (savedCg && typeof savedCg === 'object') effectiveCg = savedCg;
    }

    const urlPgObj = safeParseJSON<{ [key: string]: number }>(urlPg);
    if (urlPgObj && typeof urlPgObj === 'object') {
      effectivePg = urlPgObj;
    } else {
      const savedPg = safeParseJSON<{ [key: string]: number }>(localStorage.getItem(STORAGE_KEY_PAGEBY));
      if (savedPg && typeof savedPg === 'object') effectivePg = savedPg;
    }

    // 反映
    setSortOrder(effectiveSort);
    setPageSize(effectivePs);
    setCollapsedGroups(effectiveCg);
    setPageByGroup(effectivePg);

    // URL 同期
    let needInject = false;
    if (params.get('sort') !== effectiveSort) needInject = true;
    if (params.get('ps') !== String(effectivePs)) needInject = true;
    const cgNow = params.get('cg');
    const pgNow = params.get('pg');
    const cgStr = JSON.stringify(effectiveCg);
    const pgStr = JSON.stringify(effectivePg);
    if (cgNow !== cgStr) needInject = true;
    if (pgNow !== pgStr) needInject = true;
    if (needInject) {
      params.set('sort', effectiveSort);
      params.set('ps', String(effectivePs));
      params.set('cg', cgStr);
      params.set('pg', pgStr);
      navigate({ search: params.toString() }, { replace: true });
    }

    fetchReservations();
    fetchPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 並び順変更 → 保存＆URL 反映 */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SORT, sortOrder);
    const params = new URLSearchParams(location.search);
    if (params.get('sort') !== sortOrder) {
      params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  /** 1ページ件数変更 → 保存＆URL 反映 */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PAGESIZE, String(pageSize));
    const params = new URLSearchParams(location.search);
    if (params.get('ps') !== String(pageSize)) {
      params.set('ps', String(pageSize));
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    setPageByGroup({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  /** 折りたたみ状態の変更 → 保存＆URL 反映 */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_COLLAPSE, JSON.stringify(collapsedGroups));
    const params = new URLSearchParams(location.search);
    const next = JSON.stringify(collapsedGroups);
    if (params.get('cg') !== next) {
      params.set('cg', next);
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsedGroups]);

  /** ページ番号の変更 → 保存＆URL 反映 */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PAGEBY, JSON.stringify(pageByGroup));
    const params = new URLSearchParams(location.search);
    const next = JSON.stringify(pageByGroup);
    if (params.get('pg') !== next) {
      params.set('pg', next);
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageByGroup]);

  /** 診療完了（allowed: assigned / expired） */
  const handleComplete = async (id: number, status: string) => {
    if (status !== 'assigned' && status !== 'expired') {
      alert(t('doctor.mark_not_allowed') || 'このステータスでは完了にできません。');
      return;
    }
    try {
      await api.post('/doctor/complete_visit', { reservation_id: id });
      alert(t('doctor.mark_success') || '完了にしました。');
      await fetchReservations();
      await fetchPool();
    } catch {
      alert(t('doctor.mark_fail') || '完了に失敗しました。');
    }
  };

  /** 医师キャンセル：モーダル表示 */
  const handleCancelClick = (id: number) => {
    setCancelTargetId(id);
    setShowCancelModal(true);
  };

  /** 医师取消：确定 */
  const handleConfirmCancel = async () => {
    if (!cancelReason) {
      alert(t('doctor.select_reason_alert') || '理由を選択してください。');
      return;
    }
    const reasonMap: Record<string, string> = {
      patient_info_incomplete: t('doctor.reason_incomplete'),
      patient_not_verified: t('doctor.reason_not_verified'),
      patient_no_show: t('doctor.reason_no_show'),
      other_contact_clinic: t('doctor.reason_other'),
    };
    try {
      await api.post(`/doctor/cancel_reservation/${cancelTargetId}`, {
        reason_code: cancelReason,
        reason_text: reasonMap[cancelReason] || cancelReason,
      });
      alert(t('doctor.cancel_success') || 'キャンセルしました。');
      setShowCancelModal(false);
      setCancelReason('');
      setCancelTargetId(null);
      await fetchReservations();
      await fetchPool();
    } catch {
      alert(t('doctor.cancel_fail') || 'キャンセルに失敗しました。');
    }
  };

  /** 予約プール：Claim 事前確認 */
  const handleClaimClick = (id: number) => {
    setClaimTargetId(id);
    setShowClaimModal(true);
  };

  /** Claim 确定 */
  const handleConfirmClaim = async () => {
    try {
      if (!claimTargetId || isClaiming) return;
      setIsClaiming(true);
      await api.post(`/doctor/reservations/${claimTargetId}/claim`);
      setShowClaimModal(false);
      setClaimTargetId(null);
      await fetchReservations();
      await fetchPool();
      alert(t('doctor.claim_success') || '取得しました。');
    } catch (e: any) {
      const msg = e?.response?.data?.detail || (t('doctor.claim_failed') || '取得に失敗しました。');
      alert(msg);
    } finally {
      setIsClaiming(false);
    }
  };

  /** 退回モーダル */
  const handleReturnClick = (id: number) => {
    setReturnTargetId(id);
    setReturnReasonCode('');
    setReturnReasonText('');
    setShowReturnModal(true);
  };

  /** 退回実行 */
  const handleConfirmReturn = async () => {
    if (!returnTargetId) return;
    if (!returnReasonCode && !returnReasonText) {
      alert(t('doctor.select_reason_alert') || '理由を入力してください。');
      return;
    }
    try {
      await api.post(`/doctor/reservations/${returnTargetId}/return_to_pool`, {
        reason_code: returnReasonCode || undefined,
        reason_text: returnReasonText || undefined,
      });
      alert(t('doctor.return_success') || '予約をプールに戻しました。');
      setShowReturnModal(false);
      setReturnTargetId(null);
      setReturnReasonCode('');
      setReturnReasonText('');
      await fetchReservations();
      await fetchPool();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || (t('doctor.return_fail') || '退回に失敗しました。');
      alert(msg);
    }
  };

  const openTrackingModal = (r: Reservation) => {
    setTrackingTarget(r);
    setTrackingInput(r.tracking_number || '');
    setTrackingCarrier(r.shipping_carrier === 'yu_pack' ? 'yupack' : (r.shipping_carrier || 'yamato'));
    setShowTrackingModal(true);
  };

  const saveTrackingNumber = async () => {
    if (!trackingTarget) return;
    try {
      await api.patch(`/doctor/reservations/${trackingTarget.id}/tracking`, {
        tracking_number: trackingInput,
        shipping_carrier: trackingCarrier,
      });
      alert(t('admin.shipping_update_success') || '配送情報が更新されました');
      setShowTrackingModal(false);
      fetchReservations();
    } catch (err) {
      console.error(err);
      alert(t('admin.shipping_update_failed') || '更新失敗');
    }
  };

  /** 折叠切换 */
  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  /** 排序 */
  const sortedReservations = useMemo(() => {
    const arr = [...reservations];
    arr.sort((a, b) => {
      const ta = parseDateTime(a.date, a.time);
      const tb = parseDateTime(b.date, b.time);
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
    return arr;
  }, [reservations, sortOrder]);

  const sortedPool = useMemo(() => {
    const arr = [...pool];
    arr.sort((a, b) => {
      const ta = parseDateTime(a.date, a.time);
      const tb = parseDateTime(b.date, b.time);
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
    return arr;
  }, [pool, sortOrder]);

  /** 分组 */
  const grouped: { [key: string]: Reservation[] } = {
    pool: sortedPool,
    assigned: sortedReservations.filter((r) => r.status === 'assigned'),
    completed: sortedReservations.filter((r) => r.status === 'completed'),
    canceled: sortedReservations.filter((r) => r.status === 'canceled' || r.status === 'doctor_cancelled' || r.status === 'admin_cancelled'),
    expired: sortedReservations.filter((r) => r.status === 'expired'),
  };

  /** 只读记录弹窗 */
  const openRecordView = (r: Reservation) => {
    setRecordTarget(r);
    setShowRecordModal(true);
  };

  /** 完了済みの編集確認 */
  const confirmEditRecord = (r: Reservation) => {
    const msg = '既に診療が完了しています。記録を編集しますか？';
    if (window.confirm(msg)) {
      navigate(`/doctor/record/${r.id}`);
    }
  };

  /** ====== 移动端卡片视图 ====== */
  const renderMobileCards = (group: string, list: Reservation[]) => {
    const showRecordStatus = group === 'assigned' || group === 'completed' || group === 'expired';
    return (
      <div className="md:hidden space-y-3">
        {list.map((r) => {
          const statusInfo = showRecordStatus ? getStatus(r) : null;
          return (
            <div key={r.id} className="bg-white rounded-xl shadow p-3">
              {/* 顶部：患者 + 状态/日期时间 */}
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <PresenceDot presence={r.presence} />
                    <div className="font-semibold text-base truncate" title={r.user_name || '-'}>
                      {r.user_name || '-'}
                    </div>
                  </div>
                  <div className="text-gray-500 text-xs truncate" title={getKarteNumber(r)}>
                    {getKarteNumber(r)}
                  </div>
                </div>
                <div className="text-right text-xs text-gray-600 shrink-0">
                  <div>{r.date}</div>
                  <div>{r.time}</div>
                </div>
              </div>

              {/* 服务与问诊类型 */}
              <div className="text-sm mt-2">
                {t(`service.${r.service}`)}
                {r.visit_type && <span className="ml-1 text-gray-500 text-xs">({t(`visit_type.${r.visit_type}`)})</span>}
              </div>
              <div className="mt-1 text-xs text-gray-700">
                {(t('reserve.translation_col_header') || '翻译需求') + '：'}
                {renderTranslationNeedText(r)}
              </div>

              {/* 支払状況 */}
              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-700">
                <span>{(t('doctor.payment_status_label') || '支払状況') + '：'}</span>
                {renderPaymentStatusBadge(r)}
              </div>


              {/* 记录状态点（按钮上方） */}
              {statusInfo && (
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <Dot color={statusInfo.color} title={statusInfo.title} />
                  <span className="text-gray-600">
                    {t('doctor.record_status') || '記録入力状況'}
                  </span>
                </div>
              )}

              {/* 记录 / 问诊票 / 档案 */}
              <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <button
                  onClick={() => {
                    if (group === 'assigned' || group === 'expired') navigate(`/doctor/record/${r.id}`);
                    else if (group === 'pool') return;
                    else openRecordView(r);
                  }}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {group === 'assigned' || group === 'expired'
                    ? t('doctor.fill_record')
                    : group === 'pool'
                      ? t('na')
                      : t('doctor.view')}
                </button>

                <button
                  onClick={() => {
                    if (r.questionnaire_completed) navigate(`/doctor/questionnaire/${r.id}`);
                  }}
                  className={`px-3 py-2 border rounded-lg ${r.questionnaire_completed ? 'hover:bg-gray-50 text-coral' : 'text-gray-400'
                    }`}
                >
                  {t('doctor.questionnaire')}
                </button>

                <button
                  onClick={() => {
                    if (group !== 'pool') {
                      setProfileData(r);
                      setShowProfileModal(true);
                    }
                  }}
                  className={`px-3 py-2 border rounded-lg ${group !== 'pool' ? 'hover:bg-gray-50 text-coral' : 'text-gray-400'
                    }`}
                >
                  {t('doctor.profile')}
                </button>
              </div>

              {/* 操作按钮区 */}
              <div className="mt-3 flex flex-col gap-2">
                {['completed', 'canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status) ? (
                  <div className="text-center text-sm font-semibold text-gray-700">
                    {r.status === 'completed' && r.payment_status === 'paid' ? (
                      <button
                        onClick={() => openTrackingModal(r)}
                        className={`mt-2 px-4 py-1.5 border rounded-full text-xs font-bold transition flex items-center justify-center gap-1.5 mx-auto ${r.tracking_number ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-coral text-white border-coral hover:opacity-90'}`}
                      >
                        <FaTruck size={14} />
                        {r.tracking_number ? (t('admin.shipped') || '発送済') : (t('admin.shipping_input_tracking') || '配送入力')}
                      </button>
                    ) : (
                      <>
                        {r.status === 'completed'
                          ? t('doctor.completed')
                          : r.status === 'canceled'
                            ? t('mypage.user_cancelled')
                            : r.status === 'admin_cancelled'
                              ? t('admin.cancelled_by_admin')
                              : t('mypage.doctor_cancelled')}
                      </>
                    )}
                  </div>
                ) : group === 'pool' ? (
                  <button
                    onClick={() => handleClaimClick(r.id)}
                    className="bg-coral text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:shadow-card transition-all active:scale-95"
                  >
                    {t('doctor.claim') || 'Claim'}
                  </button>
                ) : (
                  <>
                    {(r.status === 'expired' || r.isOverdue) && (
                      <div className="text-red-600 text-xs font-semibold">
                        {t('status.overdue_short')}
                      </div>
                    )}

                    {r.status === 'assigned' && (
                      <button
                        onClick={() => navigate(`/doctor/consultation/${r.id}`)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-blue-700 transition-all active:scale-95"
                      >
                        {t('consultation.enter_consultation') || '問診室へ'}
                      </button>
                    )}

                    <button
                      onClick={() => handleComplete(r.id, r.status)}
                      className="bg-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-emerald-700 transition-all active:scale-95"
                    >
                      {t('doctor.finish')}
                    </button>

                    {r.status === 'assigned' && (
                      <button
                        onClick={() => handleReturnClick(r.id)}
                        className="bg-white border border-amber-300 text-amber-600 px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-amber-50 transition-all active:scale-95"
                        title={t('doctor.return_to_pool') || '予約プールに戻す'}
                      >
                        {t('doctor.return_to_pool') || '予約プールに戻す'}
                      </button>
                    )}

                    <button
                      onClick={() => handleCancelClick(r.id)}
                      className="bg-white border border-red-300 text-red-500 px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-red-50 transition-all active:scale-95"
                    >
                      {t('doctor.cancel')}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /** ===== 桌面端表格（新增：记录状态点 + 支払状況列） ===== */
  const renderDesktopTable = (group: string, list: Reservation[]) => {
    const rawCurrentPage = pageByGroup[group] || 1;
    const totalPages = Math.ceil(list.length / pageSize) || 1;
    const currentPage = Math.min(rawCurrentPage, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const paginated = list.slice(startIndex, startIndex + pageSize);

    const showRecordStatus = group === 'assigned' || group === 'completed' || group === 'expired';

    return (
      <>
        <table className="hidden md:table min-w-full table-auto text-[14px]">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center">
              <th className="w-auto md:w-40 whitespace-nowrap">{t('doctor.patient')}</th>
              <th className="w-auto md:w-36 whitespace-nowrap">{t('doctor.service')}</th>
              <th className="w-28 whitespace-nowrap">{t('doctor.date')}</th>
              <th className="w-20 whitespace-nowrap">{t('doctor.time')}</th>
              <th className="w-36 whitespace-nowrap">{t('reserve.translation_col_header') || '翻译需求'}</th>
              <th className="w-32 whitespace-nowrap">{t('doctor.questionnaire')}</th>
              <th className="w-28 whitespace-nowrap">{t('doctor.profile')}</th>
              <th className="w-40 whitespace-nowrap">{t('doctor.medical_record')}</th>
              <th className="w-32 whitespace-nowrap">{t('doctor.payment_status_label') || '支払状況'}</th>
              <th className="w-56 whitespace-nowrap">{t('doctor.action')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r) => {
              const statusInfo = showRecordStatus ? getStatus(r) : null;
              return (
                <tr key={r.id} className="border-t hover:bg-gray-50 text-center align-middle">
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <PresenceDot presence={r.presence} />
                      <div className="font-semibold truncate" title={r.user_name || '-'}>
                        {r.user_name || '-'}
                      </div>
                    </div>
                    <div className="text-gray-500 text-xs truncate" title={getKarteNumber(r)}>
                      {getKarteNumber(r)}
                    </div>
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap">
                    {t(`service.${r.service}`)}
                    {r.visit_type && <span className="ml-1 text-gray-500 text-xs">({t(`visit_type.${r.visit_type}`)})</span>}
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap">{r.date}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{r.time}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{renderTranslationNeedText(r)}</td>

                  <td className="px-2 py-2">
                    {r.questionnaire_completed ? (
                      <button onClick={() => navigate(`/doctor/questionnaire/${r.id}`)} className="text-coral underline">
                        {t('doctor.view')}
                      </button>
                    ) : (
                      <span className="text-gray-400">{t('doctor.not_completed')}</span>
                    )}
                  </td>

                  <td className="px-2 py-2">
                    {group === 'pool' ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <button
                        onClick={() => {
                          setProfileData(r);
                          setShowProfileModal(true);
                        }}
                        className="text-coral underline"
                      >
                        {t('doctor.view_profile')}
                      </button>
                    )}
                  </td>

                  <td className="px-2 py-2">
                    {/* 状态点（在按钮上方，便于一眼识别） */}
                    {statusInfo && (
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <Dot color={statusInfo.color} title={statusInfo.title} />
                        <span className="text-xs text-gray-600">
                          {t('doctor.record_status') || '記録入力状況'}
                        </span>
                      </div>
                    )}

                    {group === 'assigned' || group === 'expired' ? (
                      <button onClick={() => navigate(`/doctor/record/${r.id}`)} className="text-coral underline">
                        {t('doctor.fill_record')}
                      </button>
                    ) : group === 'pool' ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openRecordView(r)} className="text-coral underline">
                          {t('doctor.view')}
                        </button>
                        {group === 'completed' && (
                          <button
                            onClick={() => confirmEditRecord(r)}
                            className="text-gray-600 text-xs border rounded-lg px-2 py-0.5 hover:bg-gray-100"
                            title={t('doctor.fill_record') || '編集'}
                            aria-label={t('doctor.fill_record') || '編集'}
                          >
                            ✎
                          </button>
                        )}
                      </div>
                    )}
                  </td>

                  {/* 支払状況 */}
                  <td className="px-2 py-2">
                    {renderPaymentStatusBadge(r)}
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap break-keep">
                    {group === 'pool' ? (
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => handleClaimClick(r.id)}
                          className="bg-coral text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:shadow-card transition-all active:scale-95 w-40"
                        >
                          {t('doctor.claim') || 'Claim'}
                        </button>
                      </div>
                    ) : ['completed', 'canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status) ? (
                      <div className="flex items-center justify-center">
                        <span className="text-gray-700 font-semibold break-keep whitespace-nowrap">
                          {r.status === 'completed' && r.payment_status === 'paid' ? (
                            <button
                              onClick={() => openTrackingModal(r)}
                              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition mx-auto border ${r.tracking_number ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-coral text-white border-coral hover:opacity-90'}`}
                              title={t('admin.shipping_input_tracking') || '配送入力'}
                            >
                              <FaTruck size={14} />
                              {r.tracking_number ? (t('admin.shipped') || '発送済') : (t('admin.shipping_input_tracking') || '配送入力')}
                            </button>
                          ) : (
                            <>
                              {r.status === 'completed'
                                ? t('doctor.completed')
                                : r.status === 'canceled'
                                  ? t('mypage.user_cancelled')
                                  : r.status === 'admin_cancelled'
                                    ? t('admin.cancelled_by_admin')
                                    : t('mypage.doctor_cancelled')}
                            </>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        {(r.status === 'expired' || r.isOverdue) && (
                          <div className="text-red-600 text-xs font-semibold mb-1">
                            {t('status.overdue_short')}
                          </div>
                        )}

                        {r.status === 'assigned' && (
                          <button
                            onClick={() => navigate(`/doctor/consultation/${r.id}`)}
                            className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-blue-700 transition-all active:scale-95 w-40"
                          >
                            {t('consultation.enter_consultation') || '問診室へ'}
                          </button>
                        )}

                        <button
                          onClick={() => handleComplete(r.id, r.status)}
                          className="bg-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-emerald-700 transition-all active:scale-95 w-40"
                        >
                          {t('doctor.finish')}
                        </button>

                        {r.status === 'assigned' && (
                          <button
                            onClick={() => handleReturnClick(r.id)}
                            className="bg-white border border-amber-300 text-amber-600 px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-amber-50 transition-all active:scale-95 w-40"
                            title={t('doctor.return_to_pool') || '予約プールに戻す'}
                          >
                            {t('doctor.return_to_pool') || '予約プールに戻す'}
                          </button>
                        )}

                        <button
                          onClick={() => handleCancelClick(r.id)}
                          className="bg-white border border-red-300 text-red-500 px-6 py-2 rounded-full text-sm font-bold shadow-soft hover:bg-red-50 transition-all active:scale-95 w-40"
                        >
                          {t('doctor.cancel')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* 页码（桌面端） */}
        {totalPages > 1 && (
          <div className="hidden md:flex justify-center items-center gap-3 mt-4 pb-2">
            <button
              aria-label={t('prev') as string}
              disabled={currentPage === 1}
              onClick={() =>
                setPageByGroup({ ...pageByGroup, [group]: Math.max(1, currentPage - 1) })
              }
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
              title={t('prev') || 'Prev'}
            >
              ‹
            </button>
            <span className="text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              aria-label={t('next') as string}
              disabled={currentPage === totalPages}
              onClick={() =>
                setPageByGroup({ ...pageByGroup, [group]: Math.min(totalPages, currentPage + 1) })
              }
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
              title={t('next') || 'Next'}
            >
              ›
            </button>
          </div>
        )}
      </>
    );
  };

  /** 组块渲染 */
  const renderGroupBlock = (group: string, list: Reservation[]) => {
    return (
      <>
        {renderMobileCards(group, list)}
        {renderDesktopTable(group, list)}
      </>
    );
  };

  return (
    <>
      {/* ヘッダー行 */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
        <div className="text-xl font-bold">{t('doctor.tab_reservations')}</div>

        {/* 桌面端的分页和排序 */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('common.page_size')}</span>
            <select
              aria-label="Page size"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                setPageByGroup({});
              }}
              className="border rounded-lg px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSortOrder('desc')}
              className={`px-5 py-2 rounded-full text-sm font-bold shadow-soft transition-all active:scale-95 ${sortOrder === 'desc' ? 'bg-coral text-white shadow-card' : 'bg-white border border-border text-gray-500 hover:bg-gray-50'
                }`}
              title={t('sort.desc') || '新しい順'}
            >
              {t('sort.desc') || '新しい順'}
            </button>
            <button
              onClick={() => setSortOrder('asc')}
              className={`px-5 py-2 rounded-full text-sm font-bold shadow-soft transition-all active:scale-95 ${sortOrder === 'asc' ? 'bg-coral text-white shadow-card' : 'bg-white border border-border text-gray-500 hover:bg-gray-50'
                }`}
              title={t('sort.asc') || '古い順'}
            >
              {t('sort.asc') || '古い順'}
            </button>
          </div>
        </div>
      </div>

      {/* 各グループ */}
      {(['pool', 'assigned', 'completed', 'canceled', 'expired'] as const).map((groupKey) => {
        const list = grouped[groupKey] || [];
        const title =
          groupKey === 'pool'
            ? t('doctor.pool') || '予約プール（未割当）'
            : groupKey === 'assigned'
              ? t('doctor.assigned') || '診療待ち（割当済み）'
              : groupKey === 'completed'
                ? t('doctor.completed')
                : groupKey === 'canceled'
                  ? t('doctor.cancelled')
                  : t('mypage.expired');

        return (
          <div key={groupKey} className="mb-6 bg-white rounded-xl shadow-lg">
            <div
              className="flex justify-between items-center cursor-pointer bg-gray-200 px-4 h-12 rounded-t-xl"
              onClick={() => toggleGroup(groupKey)}
            >
              <span className="font-semibold text-lg">
                {title} ({list.length})
              </span>
              <span>{collapsedGroups[groupKey] ? '+' : '-'}</span>
            </div>
            {!collapsedGroups[groupKey] && <div className="p-3">{renderGroupBlock(groupKey, list)}</div>}
          </div>
        );
      })}

      {/* キャンセル理由モーダル */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-80 shadow-lg">
            <h2 className="text-lg font-bold mb-4">{t('doctor.select_cancel_reason')}</h2>
            <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full border rounded-lg p-2 mb-4">
              <option value="">{t('doctor.select_reason_placeholder')}</option>
              <option value="patient_info_incomplete">{t('doctor.reason_incomplete')}</option>
              <option value="patient_not_verified">{t('doctor.reason_not_verified')}</option>
              <option value="patient_no_show">{t('doctor.reason_no_show')}</option>
              <option value="other_contact_clinic">{t('doctor.reason_other')}</option>
            </select>
            <div className="text-center text-sm text-red-600 mb-4">{t('doctor.confirm_cancel_message')}</div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400">
                {t('cancel')}
              </button>
              <button onClick={handleConfirmCancel} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                {t('doctor.confirm_cancel_short')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 退回モーダル */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[420px] shadow-lg">
            <h2 className="text-lg font-bold mb-4">{t('doctor.return_to_pool_title') || 'この予約をプールに戻しますか？'}</h2>

            <label className="block text-sm mb-1">{t('doctor.select_reason') || '理由を選択'}</label>
            <select value={returnReasonCode} onChange={(e) => setReturnReasonCode(e.target.value)} className="w-full border rounded-lg p-2 mb-3">
              <option value="">{t('doctor.select_reason_placeholder') || '理由を選択してください'}</option>
              <option value="schedule_conflict">{t('doctor.reason_schedule_conflict') || 'スケジュール都合'}</option>
              <option value="sudden_absence">{t('doctor.reason_sudden_absence') || '急な不在・体調不良'}</option>
              <option value="other">{t('doctor.reason_other') || 'その他'}</option>
            </select>

            <label className="block text-sm mb-1">{t('doctor.optional_note') || '備考（任意）'}</label>
            <textarea
              value={returnReasonText}
              onChange={(e) => setReturnReasonText(e.target.value)}
              className="w-full border rounded-lg p-2 h-20 mb-3"
              placeholder={t('doctor.optional_note_ph') || '例）急遽院内対応のため 〜'}
            />

            <div className="text-xs text-gray-600 mb-3">{t('doctor.return_notice') || '※ 患者には通知されません。運用（管理者）にのみ通知されます。'}</div>

            <div className="flex justify-between gap-2">
              <button onClick={() => setShowReturnModal(false)} className="flex-1 px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400">
                {t('cancel')}
              </button>
              <button onClick={handleConfirmReturn} className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                {t('doctor.confirm_return') || 'プールに戻す'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim 確認モーダル */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-80 shadow-lg">
            <h2 className="text-lg font-bold mb-4">{t('doctor.claim_confirm_title') || 'この予約を取得しますか？'}</h2>
            <div className="text-sm text-gray-700 mb-4">{t('doctor.claim_confirm_desc') || '取得後はあなたに割り当てられ、Zoom が自動作成されます。'}</div>
            <div className="flex justify-between gap-2">
              <button
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimTargetId(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleConfirmClaim}
                disabled={isClaiming}
                className={`flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all ${isClaiming ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isClaiming ? (t('processing') || '处理中...') : (t('doctor.claim') || 'Claim')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* プロフィール表示モーダル */}
      {showProfileModal && profileData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96 shadow-lg">
            <h2 className="text-lg font-bold">{t('profile.patient_info')}</h2>
            <div className="text-sm space-y-1 mt-3">
              <div>
                {t('doctor.name')}: {profileData.user_profile?.full_name || t('na')}
              </div>
              <div>
                {t('doctor.furigana')}: {profileData.user_profile?.furigana || t('na')}
              </div>
              <div>
                {t('doctor.gender')}: {profileData.user_profile?.gender || t('na')}
              </div>
              <div>
                {t('doctor.birthday')}: {profileData.user_profile?.birth_date || t('na')}
              </div>
              <div>
                {t('doctor.phone')}: {profileData.user_profile?.phone_number || t('na')}
              </div>
              <div>
                {t('doctor.postal_code')}: {profileData.user_profile?.postal_code || t('na')}
              </div>
              <div>
                {t('doctor.address')}: {profileData.user_profile?.address || t('na')}
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowProfileModal(false)} className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 診療記録（只読）モーダル */}
      {showRecordModal && recordTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[680px] max-w-[95vw] shadow-lg">
            <h2 className="text-lg font-bold">
              {t('doctor.medical_record')} — {t(`service.${recordTarget.service}`)}{' '}
              {recordTarget.visit_type ? `(${t(`visit_type.${recordTarget.visit_type}`)})` : ''}
            </h2>

            <div className="grid grid-cols-2 gap-3 text-sm mb-3 mt-3">
              <div>
                <span className="text-gray-500">{t('doctor.date')}:</span> {recordTarget.date}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.time')}:</span> {recordTarget.time}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.patient')}:</span> {recordTarget.user_name || '-'}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.karte_number') || 'カルテ番号'}:</span> {getKarteNumber(recordTarget)}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.payment_status_label') || '支払状況'}:</span>{' '}
                {renderPaymentStatusBadge(recordTarget)}
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500 mb-1">{t('doctor.questionnaire')}</div>
                <div className="border rounded-lg p-2 bg-gray-50">
                  {recordTarget.questionnaire_completed ? t('doctor.completed') : t('doctor.not_completed')}
                </div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.diagnosis') || '診断内容'}</div>
                <div className="border rounded-lg p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.diagnosis || '-'}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.prescription') || '処方箋'}</div>
                <div className="border rounded-lg p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.prescription_text || '-'}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.notes') || 'メモ'}</div>
                <div className="border rounded-lg p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.notes || '-'}</div>
              </div>
            </div>

            <div className="flex justify-end mt-5 gap-2">
              {recordTarget.status === 'completed' && (
                <button
                  className="px-3 py-2 border rounded-lg hover:bg-gray-100"
                  title={t('doctor.fill_record') || '編集'}
                  onClick={() => confirmEditRecord(recordTarget)}
                >
                  ✎
                </button>
              )}
              <button onClick={() => setShowRecordModal(false)} className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zoom 使用步骤弹窗 */}
      <ZoomHowToModal open={showZoomHowTo} onClose={() => setShowZoomHowTo(false)} />

      {/* Zoom リンク再生成 確認モーダル */}

      {/* 物流入力モーダル */}
      {showTrackingModal && trackingTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <FaTruck className="text-coral" />
              {t('admin.tracking_input_title') || '追跡番号の登録'}
            </h3>
            <div className="bg-coral/5 p-3 rounded-xl mb-6">
              <div className="text-sm font-bold text-gray-800">{trackingTarget.user_name} 様</div>
              <div className="text-xs text-coral">ID: {trackingTarget.id}</div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">{t('admin.shipping_tracking_number') || '追跡番号'}</label>
                <input
                  type="text"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder={t('admin.tracking_input_placeholder') || '送り状番号 (半角数字)'}
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30 text-lg font-mono"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">{t('admin.shipping_carrier') || '配送業者'}</label>
                <select
                  value={trackingCarrier}
                  onChange={(e) => setTrackingCarrier(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30 bg-white"
                >
                  <option value="yamato">{t('admin.carrier_yamato') || 'ヤマト運輸'}</option>
                  <option value="sagawa">{t('admin.carrier_sagawa') || '佐川急便'}</option>
                  <option value="yupack">{t('admin.carrier_yu_pack') || '日本郵便'}</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowTrackingModal(false)}
                className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
              >
                {t('common.cancel') || 'キャンセル'}
              </button>
              <button
                onClick={saveTrackingNumber}
                className="flex-1 px-4 py-3 bg-coral text-white rounded-xl font-bold hover:opacity-90 transition shadow-lg shadow-coral/20"
              >
                {t('admin.tracking_save') || '登録する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DoctorReservations;
